<template>
  <div class="checkout-page">
    <error-boundary
      title="Something has gone wrong"
      message="If this keeps happening, please contact us."
      class="checkout-page__wrapper"
      propagate>
      <template
        #default
        class="checkout-page__content"
        @error="onCheckoutError">
        <checkout v-model="currentStepAlias"
                  class="checkout-page__checkout"
                  @beforeChangeStep="onRequestChangeStep"
                  @changeStep="onChangeStep"
                  @loading="loading = $event">
          <checkout-progress></checkout-progress>
          <div v-show="loading">
            <loading-spinner label="Loading"
                             size="2x"></loading-spinner>
          </div>
          <div v-show="!loading">
            <div class="my-steps">
              <your-details-step :stateless="submitted"
                                 class="checkout-page__your-details"
                                 heading="Your Details"
                                 :allow-login="false"
                                 :allow-full-registration="false"
                                 :web-user-type-parent-id="webUserTypeParentId"
                                 :hide-telephone="false"
                                 allow-guest-registration
                                 allow-account-details-edit>
                <template #continue-btn>
                  Continue
                </template>
                <template #register-btn>
                  Register
                </template>
                <template #back-btn>
                  Back
                </template>
              </your-details-step>
              <address-step class="checkout-page__address"
                            :stateless="submitted"
                            :currency="currency"
                            :locale="locale"
                            :web-account-org-id="webAccountOrgId"
                            :hide-previous="false"
                            default-use-invoice-for-delivery></address-step>

              <basket-step :search-type-id="searchTypeId"
                           :stateless="submitted"
                           :hide-previous="false"
                           class="checkout-page__basket">
                <template #basket="{events, ...slotProps}">
                  <styled-basket v-if="currentStepAlias === 'reviewPurchases'"
                                 v-bind="slotProps"
                                 enable-vouchers
                                 show-carriage
                                 readonly
                                 :currency="currency"
                                 :locale="locale"
                                 v-on="events">
                  </styled-basket>
                </template>
              </basket-step>

              <metadata-step :stateless="submitted"></metadata-step>

              <payment-step :terms-link="termsLink"
                            :stateless="submitted"
                            :sealed.sync="submitted"
                            :invoice-submit-button-label="invoiceSubmitButtonLabel"
                            :card-payment-icons="cardPaymentIcons"
                            :hide-previous="false">
              </payment-step>

              <confirmation-step :order-history-link="orderHistoryLink"
                                 order-history-text="See this order in my account"
                                 button-class="btn btn-accent-1"
                                 @submit="onSubmit">
              </confirmation-step>
            </div>
          </div>
        </checkout>
      </template>
    </error-boundary>
  </div>
</template>

<script>
import Vue from "vue";
// Disable linting for next line as there may be a naming clash between
// vue-scrollto and vue-scrollTo, both libraries are the same, vue-scrollto
// is just a newer version
// eslint-disable-next-line import/no-unresolved
import VueScrollTo from "vue-scrollto";
import {
  YourDetailsStep,
  AddressStep,
  BasketStep,
  PaymentStep,
  MetadataStep,
  BasketResource,
} from "spiritweb.packages.ecommerce";
import StyledBasket from "~/Components/Basket/StyledBasket.vue";
import LoadingSpinner from "~/Components/LoadingSpinner.vue";
import ConfirmationStep from "~/Components/Checkout/Steps/ConfirmationStep.vue";

Vue.use( VueScrollTo, {
  container: "#app",
  duration: 500,
  easing: "ease",
  offset: 0,
  force: true,
  cancelable: true,
  onStart: false,
  onDone: false,
  onCancel: false,
  x: false,
  y: true,
} );

export default {
  name: "CheckoutPage",
  components: {
    YourDetailsStep,
    AddressStep,
    BasketStep,
    PaymentStep,
    ConfirmationStep,
    MetadataStep,
    LoadingSpinner,
    StyledBasket,
  },
  props: {
    termsLink: {
      type: String,
      default: null,
    },
    searchTypeId: {
      type: String,
      default: null,
    },
    international: {
      type: Boolean,
      default: false,
    },
    currency: {
      type: String,
      default: "GBP",
    },
    locale: {
      type: String,
      default: "en-GB",
    },
    webAccountOrgId: {
      type: String,
      default: null,
    },
    orderHistoryLink: {
      type: String,
      default: null,
    },
    webUserTypeParentId: {
      type: Number,
      default: null,
    },
  },
  data: () => ( {
    currentStepAlias: null,
    scrollToAlias: null,
    loading: true,
    waiting: true,
    hash: "",
    submitted: false,

    cardPaymentIcons: [
      {
        src: "/Assets/images/default-payment-icons/maestro.png",
        alt: "Maestro",
      },
      {
        src: "/Assets/images/default-payment-icons/mastercard.png",
        alt: "Mastercard",
      },
      {
        src: "/Assets/images/default-payment-icons/visa.png",
        alt: "VISA",
      },
    ],
  } ),
  computed: {
    invoiceSubmitButtonLabel () {
      let result;

      if ( this.international ) {
        result = "Request Order";
      }

      return result;
    },
    isLoading () {
      return this.loading || this.waiting;
    },
  },
  watch: {
    currentStepAlias: {
      handler ( alias ) {
        window.location.hash = alias || "";
      },
    },
    hash ( val ) {
      if ( val !== this.currentStepAlias ) {
        this.currentStepAlias = val;
      }
    },
  },
  beforeCreate () {
    const promise = BasketResource.getItemCount()
      .then( ( { data } ) => {
        if ( !data ) {
          this.submitted = true;
        }
      } );

    return promise;
  },
  created () {
    window.addEventListener( "hashchange", this.onHashChange );

    BasketResource.hasBeenSubmitted()
      .then( ( response ) => {
        if ( response.data === true ) {
          this.submitted = true;
        }
      } ).finally( () => {
        const hash = this.onHashChange();
        if ( hash ) {
          this.hash = hash;
        }

        this.waiting = false;
      } );
  },
  beforeDestroy () {
    window.removeEventListener( "hashchange", this.onHashChange );
  },
  methods: {
    onSubmit ( val ) {
      this.submitted = val;
    },
    onHashChange () {
      const { hash } = window.location;
      let result = null;
      if ( hash ) {
        result = hash.replace( "#", "" );
      }
      this.hash = result;
    },
    onRequestChangeStep ( alias, resolve, reject ) {
      if ( this.scrollToAlias !== alias ) {
        // This method may be called multiple times for the same step.
        // we don't want to scroll multiple times so only scroll if the
        // step alias does not match the last step alias we scrolled to
        this.scrollToAlias = alias;

        try {
          this.$scrollTo( ".checkout-page", 300, {
            offset: -50,
            onDone: () => resolve(),
          } );
        } catch ( e ) {
          reject( e );
        }
      } else {
        resolve();
      }
    },
    onChangeStep ( step ) {
      this.currentStepAlias = step;
    },
    onCheckoutError () {
      window.location.hash = "error";
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../Styles/utils/colors.less";

.checkout-page {
  margin: 15px auto;

  .main-navigation__logo {
    margin-left: 0;
    margin-bottom: 15px;
  }

  &__content {
    margin-top: 10px;
    background: @bg-color;
    padding: 30px;
  }
}

/deep/ .checkout-progress {
  margin-bottom: 30px;

  &__list {
    position: relative;
    margin: 0;
    padding: 0 30px;
    background: @bg-color;
    justify-content: space-between;

    &::before {
      content: "";
      position: absolute;
      border-bottom: 2px solid @color-accent-1;
      left: 0;
      right: 0;
      top: 50%;
      z-index: 1;
    }
  }

  &__item {
    display: flex;
    color: #797979;

    &--disabled {
      .checkout-progress__btn {
        cursor: not-allowed;
      }
    }

    &--current {
      color: @color-accent-1;
    }
  }

  &__btn {
    position: relative;
    z-index: 2;
    background: @bg-color;
    border: 0;
    color: inherit;
    font-weight: 600;
    font-size: 16px;
    padding: 0 15px;
    margin: 0;
    -webkit-appearance: none;
  }

  @media ( max-width: 767px ) {
    visibility: hidden;
    display: none;
  }
}

/deep/ .tabbed-step {
  margin-bottom: 0 !important;
  background: #fff;
  padding: 20px 30px 30px;

  &.tabbed-step--invalid {
    .step__continue:not( .no-disabled ) {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

/deep/ .step__heading {
  margin-bottom: 30px;
}

/deep/ .step__footer-button,
/deep/ .address-step button,
/deep/ .registration-wrapper .register-wizard button:not(.close) {
  border-radius: 0;
  margin-top: 30px;
  margin-right: 5px;
  background: @color-accent-1;
  border: 0;
  color: #fff;
  padding: 10px 30px;
  text-decoration: none;
  font-weight: 600;

  &:last-child {
    margin-right: 0;
  }

  &:hover {
    background: @color-accent-1-dark;
  }
}

/deep/ .validation-message {
  margin-top: 5px;
}

.checkout-page__your-details {
  /deep/ .register-wizard .submit-button__container {
    margin-top: 0;

    button {
      margin-top: 20px;
    }
  }
}

.checkout-page__address {
  /deep/ .address-step__error {
    color: red;
  }

  /deep/ .address-list__address {
    border: #DCDCDC thin solid;

    &--selected {
      border-color: @color-accent-1;
    }
  }

  /deep/ .address-list__select {
    border-top: #DCDCDC thin solid;
  }

  /deep/ .carriage .carriage__items {
    max-height: unset;
    overflow-y: unset;
  }

  /deep/ button.address-selector__add-new {
    margin-top: 20px;
    margin-right: auto;
  }

  /deep/ .address-selector__available {
    max-height: 606px;
  }

  /deep/ .address-list__heading,
  /deep/ .carriage__heading {
    font-weight: 500;
    font-size: 1.2rem;
  }

  /deep/ .carriage__items {
    max-height: 150px;
    overflow-y: auto;
  }

  /deep/ .address-selector__search {
    button {
      margin: 0 0 10px 0;
    }

    select {
      margin-bottom: 0;
    }
  }
}

.checkout-page__basket {
  /deep/ .order-lines-list .order-line__price {
    text-align: right;

    @media ( min-width: 767px ) {
      width: 210px;
    }
  }
}
</style>
